import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ConfirmDialog, RemoteContent, Title, formatRange, useSnackbar } from '@jupi/ui'
import BookingCard from '../components/BookingCard'
import { useCancelBooking, useMyBookings, type Booking } from '../api/queries'
import { useBookingLinkState } from '../features/calendar/bookingNavigation'

export default function MyBookingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const linkState = useBookingLinkState()
  const { showSnackbar } = useSnackbar()
  const { data: bookings = [], isLoading, error } = useMyBookings()
  const cancel = useCancelBooking()
  const [toCancel, setToCancel] = useState<Booking | null>(null)

  const upcoming = bookings.filter((b) => new Date(b.endsAt) >= new Date())

  const handleCancel = async () => {
    if (!toCancel) return
    const booking = toCancel
    setToCancel(null)
    try {
      const result = await cancel.mutateAsync({ bookingId: booking.bookingId })
      showSnackbar(t('booking.cancelledOk'))
      // Worth saying: someone else's option just became their booking
      // because of this cancellation.
      if (result.promoted.length > 0) {
        showSnackbar(t('calendar.waiting', { count: result.promoted.length }), { severity: 'info' })
      }
    } catch {
      showSnackbar(t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Box>
      <Title label={t('nav.myBookings')} />

      <RemoteContent isLoading={isLoading} error={error}>
        {upcoming.length === 0 && !isLoading ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('booking.noneOwn')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {upcoming.map((booking) => (
              <Box key={booking.bookingId}>
                <BookingCard booking={booking} />
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, justifyContent: 'flex-end' }}>
                  {booking.kind === 'reservation' ? (
                    <Button size="small" onClick={() => navigate(`/bookings/${booking.bookingId}`, { state: linkState })}>
                      {t('common.edit')}
                    </Button>
                  ) : null}
                  <Button size="small" color="error" onClick={() => setToCancel(booking)}>
                    {t('common.cancel')}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </RemoteContent>

      <ConfirmDialog
        open={toCancel !== null}
        title={t('booking.cancelTitle')}
        message={
          toCancel
            ? t('booking.cancelMessage', {
                aircraft: toCancel.registration,
                when: formatRange(toCancel.startsAt, toCancel.endsAt),
              })
            : ''
        }
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.close')}
        onConfirm={handleCancel}
        onClose={() => setToCancel(null)}
      />
    </Box>
  )
}
